import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Network } from '@capacitor/network';
import { ApiAuthenticationService, ApiPaymentsService, RefreshTokenResult } from '@timechef/core/api';
import { AuthService } from '@timechef/core/auth/auth.service';
import { ILogger, LoggerService } from '@timechef/core/logging';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppErrorKeys, appErrorFromKey, ensureAppError } from '../errors/error';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  isRefreshingToken = false;
  blacklistedRoutes = [ApiAuthenticationService.PostAuthenticationLoginPath];

  private logger: ILogger;
  constructor(
    private authService: AuthService,
    private router: Router,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.getLogger('AuthInterceptor');
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return from(Network.getStatus()).pipe(
      switchMap((status) => {
        if (!status.connected) {
          this.logger.warn('No network before sending request, return error immediatly without trying to reach the server');
          return throwError(() => appErrorFromKey(AppErrorKeys.NoNetwork));
        }

        if (environment.env === 'local') {
          request = request.clone({ setHeaders: { 'ngrok-skip-browser-warning': '123' } });
        }

        return this.authService.getValidAccessToken({ allowRefresh: !isRefreshPath(request.url) }, `interceptor (${request.url})`).pipe(
          switchMap((accessToken) => {
            if (accessToken) request = this.getRequestWithToken(request, accessToken);

            return next.handle(request);
          }),
          catchError((error) => {
            return from(Network.getStatus()).pipe(
              switchMap((status) => {
                const isHttpError = error instanceof HttpErrorResponse;
                if (!status.connected || (isHttpError && error.status === 0)) {
                  this.logger.warn('No network after sending request', { network: status, error: error });
                  return throwError(() => appErrorFromKey(AppErrorKeys.NoNetwork));
                }

                if (isHttpError) {
                  if (isRefreshPath(request.url)) {
                    return from(this.authService.logout({ withRedirect: true, withAlert: false })).pipe(switchMap(() => throwError(() => error.error)));
                  }

                  if ((<HttpErrorResponse>error).status === 401 && !isLoginPath(request.url)) {
                    return this.refreshTokenAndSendRequest(request, next);
                  }

                  if (error.error.errorKey === AppErrorKeys.CompteClientNotFound && !isRefreshAccountBalance(request.url)) {
                    // Compte client not found, redirect and cancel request
                    this.router.navigate(['/link-account']);
                    return throwError(() => ensureAppError(error.error));
                  }

                  return throwError(() => ensureAppError(error.error));
                }

                return throwError(() => ensureAppError(error));
              }),
            );
          }),
        );
      }),
    );
  }

  private refreshTokenAndSendRequest(request: HttpRequest<any>, next: HttpHandler) {
    return this.authService.refreshToken().pipe(
      switchMap((result: RefreshTokenResult) => {
        return next.handle(this.getRequestWithToken(request, result.authToken!.accessToken!));
      }),
      catchError((error) => {
        return throwError(() => ensureAppError(error));
      }),
    );
  }

  private getRequestWithToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  }
}

const isRefreshPath = (url: string): boolean => url.endsWith(ApiAuthenticationService.PostAuthenticationRefreshPath);

const isLoginPath = (url: string): boolean => url.endsWith(ApiAuthenticationService.PostAuthenticationLoginPath);

const isRefreshAccountBalance = (url: string): boolean => url.endsWith(ApiPaymentsService.GetPaymentsAccountBalancePath);
